import React, { FC } from 'react'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
// eslint-disable-next-line import/no-namespace
import * as Tabs from '@radix-ui/react-tabs'
import { Text } from '@moonpig/launchpad-components'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { getDepartmentTitle } from '@moonpig/web-core-utils'
import { useFindLocaleText } from '../../text-localisation'
import { capitaliseFirstCharacter } from '../../utils'
import { SearchContext } from '../../store/slices/shared'
import { useSearchStore } from '../../store/SearchStore'

type DepartmentTabsProps = {
  currentDepartment: DepartmentsEnum
  searchContext: SearchContext
}

const StyledList = styled(Tabs.List)`
  display: flex;
  flex-wrap: nowrap;
  overflow: auto;
  gap: 3px;

  ${s({
    ml: { xs: 4, lg: 10, xxl: 12 },
  })}
`

const StyledTabs = styled(Tabs.Root)`
  ${s({
    borderBottom: 1,
    borderColor: 'colorBorder03',
    bgcolor: 'colorBackground01',
    mx: 0,
  })};
`

const StyledTrigger = styled(({ ...props }) => <Tabs.Trigger {...props} />)`
  border-radius: 8px 8px 0 0;
  ${s({
    color: 'colorTextLabel',
    bgcolor: 'colorBackground01',
    paddingX: { xs: 6, md: 8 },
    paddingY: 5,
    borderBottom: '2px solid transparent',
  })}

  :focus-visible {
    ${s({
      borderColor: 'colorInteractionSelectedState',
    })}
    outline: 3px solid;
    outline-offset: -3px;
  }

  &:focus-visible:active {
    ${s({
      borderColor: 'colorInteractionSelectedState',
      bgcolor: 'colorBackground03',
    })}
    outline: 3px solid;
    outline-offset: -3px;
  }

  &:disabled {
    ${s({
      bgcolor: 'colorBackground01',
      color: 'colorInteractionDeactivated',
    })}

    span:nth-of-type(2) {
      ${s({
        color: 'colorInteractionDeactivated',
      })}
    }
  }

  &[aria-selected='true'] {
    ${s({
      color: 'colorInteractionSelectedState',
      borderBottom: '2px solid',
      borderColor: 'colorInteractionSelectedState',
    })}

    span:nth-of-type(2) {
      ${s({
        color: 'colorTextInverted',
        bgcolor: 'colorInteractionSelectedState',
      })}
    }

    :hover {
      ${s({
        borderColor: 'colorInteractionSelectedState',
        bgcolor: 'colorBackground03',
      })}

      span:nth-of-type(2) {
        ${s({
          bgcolor: 'colorInteractionSelectedState',
        })}
      }
    }
  }

  &[aria-selected='false']:hover:enabled {
    ${s({
      bgcolor: 'colorBorder04',
      borderBottom: 2,
      borderColor: 'colorBorder02',
    })}

    span:nth-of-type(2) {
      ${s({
        bgcolor: 'colorBlack20',
      })}
    }
  }
`

const StyledCount = styled('span')`
  ${s({
    height: '20px',
    minWidth: '20px',
    paddingX: 4,
    paddingY: 3,
    ml: 4,
    borderRadius: '24px',
    bgcolor: 'colorBackground02',
    color: 'colorTextLabel',
    lineHeight: '16px',
    fontWeight: 'bold',
    fontSize: { xs: '12px', md: '16px' },
  })}
`

const departmentOrder: { [key: string]: number } = {
  ALL_CARDS: 1,
  ALL_GIFTS: 2,
  ALL_FLOWERS_AND_PLANTS: 3,
}

export const DepartmentTabs: FC<DepartmentTabsProps> = ({
  currentDepartment,
  searchContext,
}) => {
  const t = useFindLocaleText()

  const onDepartmentChange = useSearchStore(store => store.changeDepartment)
  const departmentCounts = searchContext.departmentCounts

  const departments = departmentCounts
    .map(d => {
      const departmentName = `${capitaliseFirstCharacter(
        getDepartmentTitle(d.department),
      )}`
      return {
        ...d,
        displayName: t('find.department', {
          departmentName,
        }),
        order: departmentOrder[d.department],
      }
    })
    .sort((a, b) => a.order - b.order)

  const onClick = (department: DepartmentsEnum) => {
    onDepartmentChange(department)
  }

  return (
    <StyledTabs value={currentDepartment}>
      <StyledList>
        {departments.map(department => {
          const ariaControlsId =
            `${department.displayName}-tab-panel`.toLowerCase()

          return (
            <StyledTrigger
              key={department.department}
              id={ariaControlsId}
              value={department.department}
              aria-controls={ariaControlsId}
              disabled={department.count === 0}
              onClick={() => onClick(department.department)}
            >
              <Text
                typography={'typeDisplay05'}
              >{`${department.displayName}`}</Text>
              <StyledCount>{department.count}</StyledCount>
            </StyledTrigger>
          )
        })}
      </StyledList>
    </StyledTabs>
  )
}
